import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { labelOf } from "shared/trackers/vocabulary";
import { todayLocal } from "../../whiteboard/whiteboardTracking";
import {
  checklistProgress,
  emptyTaskForm,
  formFromTask,
  isOpenTask,
  missingStandardItems,
  sortTasks,
  taskBody,
  taskFormProblem,
  type ChecklistProgress,
  type OnboardingTaskRecord,
  type TaskForm,
} from "./onboarding.types";

/** The scope that lists every client's onboarding. */
export const ALL_ONBOARDING = "all";
/** Checklist items the cross-client page reads; more is said, not hidden. */
export const TASKS_LIMIT = 1000;
const CLIENTS_LIMIT = 500;
const PEOPLE_LIMIT = 500;

type RawService = {
  service: (path: string) => {
    create: (data: object | object[]) => Promise<unknown>;
    patch: (id: string, data: object) => Promise<unknown>;
    remove: (id: string) => Promise<unknown>;
  };
};

interface ClientRow {
  id: string;
  name?: string;
  code?: string;
  status?: string;
  onboardingStep?: string | null;
  onboardingCompleted?: boolean | number | null;
  deletedAt?: string | null;
}

export interface OnboardingClientRow {
  clientId: string;
  name: string;
  stage: string | null;
  stageLabel: string;
  completed: boolean;
  progress: ChecklistProgress;
}

/**
 * Onboarding — each client's stage and checklist, with owners and dates
 * (Blake's onboarding tracker in HQ, #3075 / #2663).
 *
 * ONE class, two registrations: `onboardingViewStore` (Clients → Onboarding,
 * every client still onboarding) and `clientOnboardingViewStore` (Client 360
 * → Onboarding, pinned to the open client by the ClientDetail route).
 *
 * The items live in the `client-onboarding-tasks` ServiceStore, the clients
 * in `clients`, the people in `user`. Writes go through the RAW Feathers
 * service, so a refusal (a role, the client write gate, an owner who is not
 * live staff) reaches the form in the API's words.
 */
export default class OnboardingStore {
  rootStore: RootStore;

  scope: string = ALL_ONBOARDING;
  loading = false;
  error: string | null = null;
  clientsError: string | null = null;
  /** The page: clients whose onboarding is complete too. */
  showFinished = false;

  formOpen = false;
  editingId: string | null = null;
  form: TaskForm = emptyTaskForm();
  saving = false;
  formError: string | null = null;
  busyId: string | null = null;
  actionError: string | null = null;

  private seq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private store(path: string): ServiceStoreType | null {
    return (this.rootStore.serviceStoreManager?.getStore(path) as unknown as ServiceStoreType | null) ?? null;
  }

  private get service(): ServiceStoreType | null {
    return this.store("client-onboarding-tasks");
  }

  private get clientsService(): ServiceStoreType | null {
    return this.store("clients");
  }

  private get usersService(): ServiceStoreType | null {
    return this.store("user");
  }

  get isClientScope(): boolean {
    return this.scope !== ALL_ONBOARDING;
  }

  get today(): string {
    return todayLocal();
  }

  /** The scope's items, in checklist order. */
  get tasks(): OnboardingTaskRecord[] {
    const all = ((this.service?.entities ?? []) as unknown as OnboardingTaskRecord[]).filter((t) => t?.id != null);
    const scoped = this.isClientScope ? all.filter((t) => String(t.clientId).toLowerCase() === this.scope.toLowerCase()) : all;
    return sortTasks(scoped);
  }

  get progress(): ChecklistProgress {
    return checklistProgress(this.tasks, this.today);
  }

  /** The cross-client read hit its cap: some clients' items may be missing. */
  get capped(): boolean {
    return !this.isClientScope && this.tasks.length >= TASKS_LIMIT;
  }

  /** The page's rows: each live client still onboarding (or, switched, every one with a stage or items). */
  get clientRows(): OnboardingClientRow[] {
    const clients = (this.clientsService?.entities ?? []) as unknown as ClientRow[];
    const byClient = new Map<string, OnboardingTaskRecord[]>();
    for (const t of this.tasks) {
      const key = String(t.clientId).toLowerCase();
      byClient.set(key, [...(byClient.get(key) ?? []), t]);
    }
    return clients
      .filter((c) => !c.deletedAt)
      .map((c) => {
        const tasks = byClient.get(String(c.id).toLowerCase()) ?? [];
        const completed = c.onboardingCompleted === true || c.onboardingCompleted === 1;
        return {
          clientId: String(c.id),
          name: c.name ?? "—",
          stage: c.onboardingStep ?? null,
          stageLabel: c.onboardingStep ? labelOf("onboardingStage", c.onboardingStep) : "Not started",
          completed,
          progress: checklistProgress(tasks, this.today),
        };
      })
      .filter((row) => (this.showFinished ? row.stage !== null || row.progress.total > 0 : !row.completed && (row.stage !== null || row.progress.total > 0)))
      .sort((a, b) => b.progress.overdue - a.progress.overdue || a.name.localeCompare(b.name));
  }

  /** People an item can be owned by: live staff, by name. */
  get peopleOptions(): Array<{ id: number; name: string }> {
    const rows = (this.usersService?.entities ?? []) as unknown as Array<{
      id: number | string;
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
      isValidated?: boolean | number | null;
    }>;
    return rows
      .filter((u) => u.isValidated !== false && u.isValidated !== 0)
      .map((u) => ({ id: Number(u.id), name: [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email || `User ${u.id}` }))
      .filter((p) => Number.isSafeInteger(p.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * May this person change the checklist? On Client 360 it is the client's own
   * answer (role, and the client write gate's verdict the Overview carries);
   * the page itself changes nothing.
   */
  get canEdit(): boolean {
    if (!this.isClientScope) return false;
    const detail = (this.rootStore as unknown as { clientDetailStore?: { canWrite?: boolean } }).clientDetailStore;
    return detail?.canWrite === true;
  }

  /** Stages without an item yet — what "Add the standard checklist" would add. */
  get missingStandard() {
    return this.isClientScope ? missingStandardItems(this.scope, this.tasks) : [];
  }

  setShowFinished(value: boolean) {
    this.showFinished = value;
  }

  async initialize(scope: string = ALL_ONBOARDING): Promise<void> {
    this.scope = scope || ALL_ONBOARDING;
    await Promise.all([this.load(), this.isClientScope ? this.loadPeople() : this.loadClients()]);
  }

  /** Re-read the scope's items. Never rejects: a failure lands on `error`. */
  async load(): Promise<void> {
    const scope = this.scope;
    const seq = ++this.seq;
    const ended = sessionMark(this.rootStore);
    this.loading = true;
    this.error = null;
    try {
      const query: Record<string, unknown> =
        scope === ALL_ONBOARDING ? { $limit: TASKS_LIMIT, $sort: { dueDate: 1 } } : { clientId: scope, $limit: 200, $sort: { sortOrder: 1 } };
      await findOrThrow(this.service, { query });
    } catch (err) {
      if (seq !== this.seq || ended() || isSessionEnded(err)) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "The onboarding checklist could not be loaded.");
      });
    } finally {
      if (seq === this.seq && !ended()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  async loadClients(): Promise<void> {
    const ended = sessionMark(this.rootStore);
    this.clientsError = null;
    try {
      await findOrThrow(this.clientsService, { query: { $limit: CLIENTS_LIMIT } });
    } catch (err) {
      if (ended() || isSessionEnded(err)) return;
      runInAction(() => {
        this.clientsError = loadErrorMessage(err, "Clients could not be loaded.");
      });
    }
  }

  /** The owner picker's people. Tolerant: a failure only leaves the picker with fewer names. */
  async loadPeople(): Promise<void> {
    if (this.peopleOptions.length > 0) return;
    await this.usersService?.find({ query: { $limit: PEOPLE_LIMIT } })?.catch?.(() => undefined);
  }

  // ── the form ─────────────────────────────────────────────────────────────

  startCreate() {
    this.editingId = null;
    this.form = emptyTaskForm(this.isClientScope ? this.scope : "");
    this.formError = null;
    this.formOpen = true;
  }

  startEdit(task: OnboardingTaskRecord) {
    this.editingId = task.id;
    this.form = formFromTask(task);
    this.formError = null;
    this.formOpen = true;
  }

  closeForm() {
    this.formOpen = false;
    this.formError = null;
  }

  setField<K extends keyof TaskForm>(field: K, value: TaskForm[K]) {
    this.form = { ...this.form, [field]: value };
    this.formError = null;
  }

  get formProblem(): string | null {
    return taskFormProblem(this.form);
  }

  private get api(): RawService | undefined {
    return this.rootStore.app as unknown as RawService | undefined;
  }

  private async write(run: (api: RawService) => Promise<unknown>, failure: string, onError: (message: string) => void): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    try {
      const api = this.api;
      if (!api) throw new Error("Not connected to the API — reload and try again.");
      await run(api);
      if (ended()) return false;
      await this.load();
      return true;
    } catch (err) {
      if (!ended() && !isSessionEnded(err)) {
        const message = loadErrorMessage(err, failure);
        runInAction(() => onError(message));
      }
      return false;
    }
  }

  async save(): Promise<boolean> {
    const problem = this.formProblem;
    if (problem) {
      this.formError = problem;
      return false;
    }
    const ended = sessionMark(this.rootStore);
    this.saving = true;
    this.formError = null;
    const body = taskBody(this.form);
    const editingId = this.editingId;
    const clientId = this.form.clientId;
    const ok = await this.write(
      (api) =>
        editingId ? api.service("client-onboarding-tasks").patch(editingId, body) : api.service("client-onboarding-tasks").create({ ...body, clientId }),
      "The checklist item could not be saved.",
      (message) => {
        this.formError = message;
      },
    );
    if (ended()) return false;
    runInAction(() => {
      this.saving = false;
      if (ok) {
        this.formOpen = false;
        this.editingId = null;
      }
    });
    return ok;
  }

  /** A quick status change from the row. */
  async setStatus(task: OnboardingTaskRecord, status: string): Promise<boolean> {
    if (task.status === status) return true;
    const ended = sessionMark(this.rootStore);
    this.busyId = task.id;
    this.actionError = null;
    const ok = await this.write(
      (api) => api.service("client-onboarding-tasks").patch(task.id, { status }),
      "The item could not be updated.",
      (message) => {
        this.actionError = message;
      },
    );
    if (ended()) return false;
    runInAction(() => {
      this.busyId = null;
    });
    return ok;
  }

  async remove(task: OnboardingTaskRecord): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    this.busyId = task.id;
    this.actionError = null;
    const ok = await this.write(
      (api) => api.service("client-onboarding-tasks").remove(task.id),
      "The item could not be removed.",
      (message) => {
        this.actionError = message;
      },
    );
    if (ended()) return false;
    runInAction(() => {
      this.busyId = null;
    });
    return ok;
  }

  /** One item for each stage this client has none for, in one call. */
  async addStandardChecklist(): Promise<boolean> {
    const items = this.missingStandard;
    if (items.length === 0) return true;
    const ended = sessionMark(this.rootStore);
    this.busyId = "standard";
    this.actionError = null;
    const ok = await this.write(
      (api) => api.service("client-onboarding-tasks").create(items),
      "The standard checklist could not be added.",
      (message) => {
        this.actionError = message;
      },
    );
    if (ended()) return false;
    runInAction(() => {
      this.busyId = null;
    });
    return ok;
  }

  /** Open items on the page, for the summary line. */
  get openCount(): number {
    return this.tasks.filter(isOpenTask).length;
  }

  reset() {
    this.seq += 1;
    this.scope = ALL_ONBOARDING;
    this.loading = false;
    this.error = null;
    this.clientsError = null;
    this.showFinished = false;
    this.formOpen = false;
    this.editingId = null;
    this.form = emptyTaskForm();
    this.saving = false;
    this.formError = null;
    this.busyId = null;
    this.actionError = null;
  }
}
